import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { projectService, type ConversationSummaryDto } from '../api/services/project.service';

/**
 * Shared cache for a scope's conversation list (the sidebar "Recent chats").
 *
 * Previously each page under a scope fetched — or didn't fetch — this list on
 * its own: the conversation page held it in local state, while the scope
 * sub-pages (Files / Artifacts) passed an empty array, so recent chats vanished
 * the moment you left the conversation page. Keying the list under one
 * React-Query entry means it's fetched once on the first visit to the scope and
 * reused across every route beneath it (conversation, files, artifacts).
 */
export const scopeConversationsKey = (
  startupId?: string | null,
  scopeId?: string | null,
) => ['scope-conversations', startupId ?? '', scopeId ?? ''] as const;

export function useScopeConversations(startupId?: string | null, scopeId?: string | null) {
  return useQuery<ConversationSummaryDto[]>({
    queryKey: scopeConversationsKey(startupId, scopeId),
    queryFn: () => projectService.listScopeConversations(startupId as string, scopeId as string),
    enabled: !!startupId && !!scopeId,
    // Short window so switching between a scope's sub-pages is a cache hit, but
    // a returning visit still picks up chats created elsewhere reasonably soon.
    staleTime: 30_000,
  });
}

/**
 * Invalidate a scope's conversation list so the next read refetches — call after
 * a conversation is created / renamed / deleted so every route under the scope
 * reflects it.
 */
export function useInvalidateScopeConversations() {
  const queryClient = useQueryClient();
  return useCallback(
    (startupId?: string | null, scopeId?: string | null) =>
      queryClient.invalidateQueries({ queryKey: scopeConversationsKey(startupId, scopeId) }),
    [queryClient],
  );
}
